import { useRouter } from "next/router";
import { ClerkProvider } from "@clerk/nextjs";
import { Toaster } from "react-hot-toast";
import { useEffect, useState } from "react";
import ErrorBoundary from "../components/ErrorBoundary";
import { AuthProvider } from "../contexts/AuthContext";
import { DatabaseProvider } from "../contexts/DatabaseContext";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import "../styles/globals.css";
import "../styles/image-protection.css";
import Head from "next/head";
import NextNProgress from "nextjs-progressbar";
import dynamic from 'next/dynamic';
import { useHydration } from "../lib/useHydration";
import DebugInfo from "../components/debug/DebugInfo";
import CookieBanner from "../components/ui/CookieBanner";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { Analytics } from "@vercel/analytics/next";
import PWAInstallPrompt from "../components/pwa/PWAInstallPrompt";
import PWAUpdatePrompt from "../components/pwa/PWAUpdatePrompt";
import NetworkStatus from "../components/pwa/NetworkStatus";
// import { useSocketInitializer } from "../lib/useSocketInitializer"; // Removed - using external WebSocket server

// Import monitoring conditionally to avoid build errors
const initMonitoring = () => {
  if (typeof window !== "undefined") {
    // Use requestIdleCallback to initialize monitoring during browser idle time
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => {
        // Load monitoring module (if available)
        import("../lib/monitoring")
          .then(({ initClientMonitoring }) => {
            initClientMonitoring();
          })
          .catch((err) => {
            // Non-critical, can safely continue without monitoring
            console.log("Monitoring module not available");
          });
          
        // Initialize the safer webpack monitoring
        import("../lib/next-module-helper")
          .then(({ monitorWebpackCompilation }) => {
            if (typeof monitorWebpackCompilation === 'function') {
              monitorWebpackCompilation();
            }
          })
          .catch(() => {
            // Also non-critical
          });
      });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      setTimeout(() => {
        import("../lib/monitoring")
          .then(({ initClientMonitoring }) => {
            initClientMonitoring();
          })
          .catch(() => {});
      }, 2000); // Delay by 2 seconds
    }
  }
};

// Routes that should not show the standard layout
const noLayoutRoutes = [
  "/auth/sign-in",
  "/auth/sign-up",
  "/auth/sign-out",
  "/auth/callback",
  "/auth/[[...clerk]]",
  "/dashboard/admin",
  "/dashboard/agent",
];

function isNoLayoutRoute(pathname) {
  return noLayoutRoutes.some(route => {
    if (route.includes("[") && route.includes("]")) {
      const baseRoute = route.split("[")[0];
      return pathname.startsWith(baseRoute);
    }
    // For regular routes, check if pathname starts with the route
    return pathname.startsWith(route);
  });
}

// Create a client-side only component for dev tools
const DevTools = dynamic(
  () => import('../components/debug/DevTools').then(mod => mod.default),
  { ssr: false }
);

// Initialize socket connection
const initSocketConnection = () => {
  if (typeof window !== 'undefined') {
    // Load socket client when needed
    import('../lib/socket-client')
      .then(({ initializeSocket }) => {
        initializeSocket();
      })
      .catch((err) => {
        console.log("Socket initialization error:", err);
      });

    // Test WebSocket connection in development
    if (process.env.NODE_ENV === 'development') {
      import('../lib/socket-test')
        .then(({ runConnectionTest }) => {
          setTimeout(() => {
            runConnectionTest();
          }, 3000); // Test after 3 seconds
        })
        .catch((err) => {
          console.log("Socket test error:", err);
        });
    }
  }
};

// PWA is automatically handled by next-pwa
const initPWA = () => {
  if (typeof window !== 'undefined') {
    console.log('[PWA] next-pwa handles service worker registration automatically');
  }
};

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);
  const isHydrated = useHydration();
  
  // Socket.IO is now handled externally on Render
  // useSocketInitializer(); // Removed - using external WebSocket server

  // Initialize monitoring safely on component mount with a delay
  useEffect(() => {
    setIsMounted(true);
    
    // Delay non-critical operations
    const timer = setTimeout(() => {
      initMonitoring();
      initSocketConnection(); // Initialize socket connection
      initPWA(); // Initialize PWA features
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Use layout from component if available, otherwise use default layout
  const getLayout = Component.getLayout || ((page) => page);
  const showStandardLayout = !isNoLayoutRoute(router.pathname);

  // Don't render layout-sensitive content until after hydration
  if (!isMounted || !isHydrated) {
    return (
      <ClerkProvider
        {...pageProps}
        navigate={(to) => router.push(to)}
        publishableKey={process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}
      >
        <div className="min-h-screen" />
      </ClerkProvider>
    );
  }
  return (
    <ErrorBoundary>
      <ClerkProvider
        {...pageProps}
        navigate={(to) => router.push(to)}
        publishableKey={process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}
      >
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
        </Head>

        <DatabaseProvider>
          <AuthProvider>
            <div className="flex min-h-screen flex-col">
              <NextNProgress />
              <Toaster position="top-right" />
              
              {/* PWA Components */}
              <NetworkStatus />
              <PWAUpdatePrompt />
              
              {showStandardLayout && <Header />}
              <main className="flex-1 pt-12">
                {getLayout(<Component {...pageProps} />)}
              </main>
              {showStandardLayout && <Footer />}
              
              {/* PWA Install Prompt */}
              <PWAInstallPrompt />
              
              {process.env.NODE_ENV === 'development' && <DevTools />}
              <DebugInfo />
              <CookieBanner />
              <SpeedInsights />
              <Analytics />
            </div>
          </AuthProvider>
        </DatabaseProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
